import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import Group from 'components/utils/Group'
import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { usePlacesBySearchPhrase } from 'hooks/usePlace'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { TRIP_PLACE_TYPEAHEAD_TYPES } from 'tripPlanner/config/search'
import { useTrendingDestinations } from 'tripPlanner/pages/trip/useTripLandingPageTrendingDestinations'
import { destinations } from 'tripPlanner/pages/trip/TripLandingPageTrendingDestinations'
import ImageTileCard from 'tripPlanner/components/EntryPoints/Common/Tile/ImageTileCard'
import { TrendingDestination } from 'home/components/TrendingDestinations/constants'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { take } from 'lib/array/arrayUtils'
import Sheet from 'components/utils/Sheet'
import AITripDestinationListItem, { AITripDestinationListItemClickHandler } from './AITripDestinationListItem'
import * as Analytics from 'analytics/analytics'
import { Locations, OccupancyOption } from 'tripPlanner/pages/trip/GenerateTripPage'
import InputChip from 'LuxKit/Chips/InputChip'
import { rem } from 'polished'
import styled from 'styled-components'
import { useSuggestedLocations, SuggestedLocationItem } from 'tripPlanner/hooks/api/suggestedLocations'
import LoadingImageTileCard from './Common/Tile/LoadingImageTileCard'

const InputChipEmptySpace = styled.div`
min-height: ${rem(28)};
`

interface Props {
  location?: string
  onLocationSelect?: (location: string) => void
  onNext?: () => void
  isGeneratingTrip?: boolean
  stepNumber?: number
  totalSteps?: number
  onAddLocation?: (params: {
    locationName: string;
    startDate?: Date;
    endDate?: Date;
    occupancy?: OccupancyOption;
    duration?: number;
    destinationId?: string
  }) => void
  locations?: Array<Locations>
  onRemoveLocation?: (locationName: string) => void
}

function AITripDestinationSelectionV2(props: Props) {
  const { onLocationSelect, stepNumber, totalSteps, onAddLocation, locations, onRemoveLocation } = props
  const [searchPhrase, setSearchPhrase] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const [searchResults, isLoading, hasError] = usePlacesBySearchPhrase(searchPhrase, {
    placeTypes: TRIP_PLACE_TYPEAHEAD_TYPES,
    limit: 10,
  })

  const { finalDestinations } = useTrendingDestinations(destinations)

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'trip_destination',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhrase(event.currentTarget.value)
  }, [])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleDropdownClose = useCallback(() => {
    setIsFocused(false)
  }, [])

  const handleDestinationSelect = useCallback<AITripDestinationListItemClickHandler>((selectedDestination) => {
    setIsFocused(false)
    const fullLocation = selectedDestination.format.secondaryText
      ? `${selectedDestination.format.mainText}, ${selectedDestination.format.secondaryText}`
      : selectedDestination.format.mainText
    const shortLocation = selectedDestination.format.mainText
    const destinationId = selectedDestination.value
    onLocationSelect?.(fullLocation)
    onAddLocation?.({
      locationName: shortLocation,
      destinationId: destinationId,
    })
    setSearchPhrase('')
  }, [onLocationSelect, onAddLocation])

  const handleClear = useCallback(() => {
    setSearchPhrase('')
  }, [])

  const canShowDropdown = isFocused && searchPhrase.length >= 2 && !hasError
  const hasResults = searchResults.length > 0

  const handleTrendingDestinationClick = useCallback((destination: TrendingDestination) => {
    setSearchPhrase('')
    setIsFocused(false)
    onLocationSelect?.(destination.name)
    onAddLocation?.({
      locationName: destination.name,
      destinationId: destination.destinationId,
    })
  }, [onLocationSelect, onAddLocation])

  const handleRemoveLocationClick = useCallback((event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    const locationName = event.currentTarget.dataset.locationName
    if (locationName) {
      onRemoveLocation?.(locationName)
    }
  }, [onRemoveLocation])

  const handleSuggestedLocationClick = useCallback((suggestedLocation: SuggestedLocationItem) => {
    setSearchPhrase('')
    setIsFocused(false)
    onLocationSelect?.(suggestedLocation.name)
    onAddLocation?.({
      locationName: suggestedLocation.name,
      destinationId: suggestedLocation.destinationId,
    })
  }, [onLocationSelect, onAddLocation])
  const selectedLocationNames = useMemo(() => locations?.map(location => location.locationName) || [], [locations])
  const { data: suggestedLocations, isLoading: isLoadingSuggestedLocations } = useSuggestedLocations({ locations: selectedLocationNames })

  useEffect(() => {
    if (!isLoadingSuggestedLocations && suggestedLocations && suggestedLocations.length > 0) {
      suggestedLocations.forEach((location) => {
        const hasImage = !!(location.imageId || location.url)
        Analytics.trackClientEvent({
          subject: hasImage ? 'has_suggested_destination_image' : 'no_suggested_destination_image',
          action: location.destinationId || location.name,
          category: 'trip_planner',
          type: 'nonInteraction',
        })
      })
    }
  }, [suggestedLocations, isLoadingSuggestedLocations])

  return (
    <Group direction="vertical" gap={40}>
      <Group direction="vertical" gap={12}>
        <BodyText variant="large" weight="bold">{stepNumber || 1} of {totalSteps || 10}</BodyText>
        <Heading variant="heading1">
          Trip <i> Destinations</i>
        </Heading>
        <BodyText variant="large">
          Select one or more destinations for your trip.
        </BodyText>
        <SearchFormTextField
          placeholder="Search by destination"
          value={searchPhrase}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onClear={handleClear}
          ref={inputRef}
          label="Where"
        />

        <DropdownList
          size="fill-anchor"
          open={canShowDropdown}
          anchorRef={inputRef}
          triggerRef={inputRef}
          onClose={handleDropdownClose}
        >
          {isLoading && <LoadingIndicator inline />}

          {!isLoading && hasResults && searchResults.map((result) => (
            <AITripDestinationListItem
              key={result.value}
              item={result}
              onClick={handleDestinationSelect}

            />
          ))}

          {!isLoading && !hasResults && searchPhrase.length >= 2 && (
            <Sheet padding={16}>

              <BodyText variant="medium" colour="neutral-two">
                No destinations found for "{searchPhrase}"
              </BodyText>
              <BodyText variant="medium" colour="neutral-two">
                Try a different search term
              </BodyText>
            </Sheet>
          )}
        </DropdownList>
        {locations && locations.length > 0 && (
          <Group direction="horizontal" gap={12} wrap="wrap">
            {locations.map((locationItem: Locations) => (
              <InputChip
                key={locationItem.locationName}
                variant="outline"
                onClick={handleRemoveLocationClick}
                data-location-name={locationItem.locationName}
              >
                {locationItem.locationName}
              </InputChip>
            ))}
          </Group>
        )}
        {!locations || locations.length === 0 && (
          <InputChipEmptySpace />
        )}
      </Group>

      <Group direction="vertical" gap={16}>
        {selectedLocationNames.length > 0 && (
          <>
            <BodyText variant="medium" weight="bold">Suggested destinations for your trip</BodyText>
            <TileCardsGroup>
              {isLoadingSuggestedLocations &&
                <>
                  <LoadingImageTileCard />
                  <LoadingImageTileCard />
                  <LoadingImageTileCard />
                  <LoadingImageTileCard />
                  <LoadingImageTileCard />
                  <LoadingImageTileCard />
                </>}
              {!isLoadingSuggestedLocations && suggestedLocations && suggestedLocations.length > 0 && suggestedLocations.map((suggestedLocation) => (
                <ImageTileCard
                  key={suggestedLocation.destinationId || suggestedLocation.name}
                  title={suggestedLocation.name}
                  imageId={suggestedLocation.imageId}
                  value={suggestedLocation}
                  onClick={handleSuggestedLocationClick}
                  layout="auto"
                />
              ))}
              {!isLoadingSuggestedLocations && (!suggestedLocations || suggestedLocations.length === 0) && (
                <BodyText variant="medium" colour="neutral-two">
                  No suggested destinations available
                </BodyText>
              )}
            </TileCardsGroup>
          </>
        )}
        {selectedLocationNames.length === 0 && (
          <>
            <BodyText variant="medium" weight="bold"> Or explore trending destinations</BodyText>
            <TileCardsGroup>
              {take(finalDestinations, 6).map((trendingDestination) => (
                <ImageTileCard
                  key={trendingDestination.destinationId}
                  title={trendingDestination.name}
                  imageId={trendingDestination.imageId}
                  value={trendingDestination}
                  onClick={handleTrendingDestinationClick}
                  layout="auto"
                />
              ))}
            </TileCardsGroup>
          </>
        )}
      </Group>
    </Group>
  )
}

export default AITripDestinationSelectionV2
